import { Text as RNText, type TextProps } from "react-native";
import {
  fontFamilies,
  fontWeights,
  typography,
  useTheme,
} from "~/styles/theme";

type Weight = keyof typeof fontWeights;

type Props = TextProps & {
  weight?: Weight;
  color?: string;
  align?: "left" | "center" | "right";
  tabular?: boolean;
};

function familyFor(weight: Weight) {
  switch (weight) {
    case "bold":
      return fontFamilies.bold;
    case "semibold":
      return fontFamilies.semibold;
    case "medium":
      return fontFamilies.medium;
    default:
      return fontFamilies.regular;
  }
}

function make(size: number, defaultWeight: Weight = "regular") {
  return function Sized({
    weight = defaultWeight,
    color,
    align,
    tabular,
    style,
    ...rest
  }: Props) {
    const { colors } = useTheme();
    return (
      <RNText
        style={[
          {
            fontSize: size,
            lineHeight: size * 1.24,
            fontFamily: familyFor(weight),
            color: color ?? colors.ink,
            textAlign: align,
            letterSpacing: size >= 24 ? -size * 0.01 : 0,
          },
          tabular ? { fontVariant: ["tabular-nums"] } : null,
          style,
        ]}
        {...rest}
      />
    );
  };
}

export const DisplayText = make(typography.display, "semibold");
export const Heading1 = make(typography.heading1, "semibold");
export const Heading2 = make(typography.heading2, "semibold");
export const Heading3 = make(typography.heading3, "semibold");
export const BodyText = make(typography.body, "regular");
export const LabelText = make(typography.label, "medium");
export const CaptionText = make(typography.caption, "medium");

/** The big counter figure — always tabular, always the heaviest weight. */
export function FigureText({
  style,
  color,
  ...rest
}: TextProps & { color?: string }) {
  const { colors } = useTheme();
  return (
    <RNText
      style={[
        {
          fontFamily: fontFamilies.bold,
          fontVariant: ["tabular-nums"],
          color: color ?? colors.ink,
          letterSpacing: -1,
        },
        style,
      ]}
      {...rest}
    />
  );
}
